import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { PublicProducerEvent } from '@/types/publicEvent';
import { isActivityPubliclyVisible } from '@/lib/events/publicVisibility';
import { fetchCourseOccurrenceInfo, resolveCourseVisibilityRow } from '@/lib/events/resolveCourseOccurrences';

export function usePublicProducerEvents(producerId: string | undefined) {
  return useQuery({
    queryKey: ['public-producer-events-rpc', producerId],
    queryFn: async (): Promise<PublicProducerEvent[]> => {
      if (!producerId) return [];

      const { data, error } = await supabase
        .rpc('get_public_events_with_bookings' as any, {
          p_producer_id: producerId,
          p_limit: 500,
        });

      if (error) throw error;

      const rows = (data || []) as Array<any>;
      const courseIds = rows.filter((row) => row.event_type === 'course').map((row) => row.id);
      const courseInfo = await fetchCourseOccurrenceInfo(courseIds);
      const resolvedRows = rows
        .map((row) => resolveCourseVisibilityRow(row, courseInfo.get(row.id)))
        .filter((row): row is any => row !== null);
      const now = new Date();

      return resolvedRows
        .filter((row) => ['event', 'course'].includes(row.event_type || 'event'))
        .filter((row) =>
          isActivityPubliclyVisible(
            {
              timingMode: row.timing_mode || 'date_time',
              startDate: row.start_date,
              endDate: row.end_date,
            },
            now,
          ),
        )
        .map((row) => ({
          event_id: row.id,
          title: row.title,
          short_description: row.short_description ?? null,
          cover_image_url: null,
          event_type: row.event_type,
          timing_mode: row.timing_mode,
          outing_tags: row.outing_tags ?? [],
          price_ore: row.price_ore ?? 0,
          currency: row.currency ?? 'SEK',
          is_free: row.is_free ?? false,
          start_date: row.start_date,
          end_date: row.end_date,
          is_recurring: (row.occurrences_count ?? 0) > 1,
          occurrences_count: row.occurrences_count ?? 0,
          next_occurrence_at: row.start_date,
          next_occurrence_seats_left: row.spots_left ?? 0,
          min_seats_left: row.spots_left ?? 0,
          max_seats_left: row.spots_left ?? 0,
          location: row.custom_address ?? null,
          venue_text: row.custom_address ?? null,
          includes_farm_sale: row.includes_farm_sale ?? false,
          includes_farm_sale_alcohol: row.includes_farm_sale_alcohol ?? false,
        } as PublicProducerEvent));
    },
    enabled: !!producerId,
    staleTime: 30_000,
  });
}
